import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, X } from "lucide-react";

/**
 * Sticky banner shown at the very top of the app when the current session is
 * an impersonation. Founder can click "Exit impersonation" to restore their
 * original admin session.
 *
 * Rendered inside DashboardLayout so it covers every internal page.
 */
export default function ImpersonationBanner() {
  const { user, refreshMe } = useAuth();
  if (!user?.impersonating) return null;

  const exit = async () => {
    try {
      await api.post("/admin/impersonate/exit");
      await refreshMe();
      toast.success("Back to your admin session");
      // Route to the founder's home so we don't stay on a page they may not own
      window.location.assign("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-40 w-full text-white shadow-md"
      style={{ background: "linear-gradient(90deg,#B45309,#D97706)" }}
    >
      <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="min-w-0 text-sm">
            <span className="opacity-90">Impersonating </span>
            <span className="font-semibold break-all">{user.email}</span>
          </div>
        </div>
        <button
          onClick={exit}
          data-testid="impersonation-exit-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#B45309] hover:bg-white/95 text-xs sm:text-sm font-semibold shadow-sm"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.5} /> Exit impersonation
        </button>
      </div>
    </div>
  );
}
